import * as Sentry from "@sentry/react";
import { DEV_DSN_KEY, PROD_DSN_KEY } from "./constants";
import sentryEventScopeTagsIntegration from "./sentryEventScopeTagsIntegration";
import sentrySetUserIDIntegration from "./sentryUserIntegration";

const transformArray = (stringsArray = []) => {
    return stringsArray.map((item) => {
        if (item.regex) {
            return new RegExp(item.regex);
        }
        return item;
    });
};

const getBeforeSendFilter = (stringFilter) => {
    const filter = stringFilter || "pubads_impl|viafoura";
    return new RegExp(filter);
};

const matchFilter = (item, regEx) => {
    return Object.values(item).some((value) => regEx.test(value));
};

const removeUuidsFromEventTitle = (exception) => {
    return exception?.value?.replace(
        /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/,
        "********-****-****-****-************"
    );
};

const beforeSend = (regEx) => {
    return (event) => {
        if (event.tags?.component === "@cbc/macaw") {
            return null;
        }
        const exception = event.exception?.values?.[0];
        if (exception) {
            const frames = exception.stacktrace?.frames || [];
            if (frames.some((item) => matchFilter(item, regEx))) {
                return null;
            }
            if (regEx.test(exception.value)) {
                return null;
            }
            const censoredExceptionValue = removeUuidsFromEventTitle(exception);
            if (censoredExceptionValue) {
                exception.value = censoredExceptionValue;
            }
        }
        return event;
    };
};

const getErrorContext = (context) => {
    if (!context) {
        return "No additional context.";
    }
    if (typeof context === "string") {
        return context;
    }
    try {
        return JSON.stringify(context);
    } catch {
        return context;
    }
};

export const sentryCaptureException = (error) => {
    Sentry.captureException(error, {
        extra: {
            context: getErrorContext(error.extraData),
        },
    });
};

export const initializeSentry = ({ sentry }, history) => {
    if (!sentry) {
        console.error(
            "(initializeSentry) Unable to initialize Sentry: FeatureFlag not found!"
        );
        return;
    }
    const host = window.location.host;
    const appVersion = window.__APP_VERSION__;
    const tracesSampleRate = Number.isNaN(Number(sentry.tracesSampleRate))
        ? 0.1
        : Number(sentry.tracesSampleRate);
    const allowUrls = transformArray(sentry.allowUrls);
    const denyUrls = transformArray(sentry.denyUrls);
    const ignoreErrors = transformArray(sentry.ignoreErrors);
    const beforeSendFilter = getBeforeSendFilter(sentry.beforeSendFilter);
    const isSentryDevDebug = window.__IS_SENTRY_DEV_DEBUG__;

    let environment = null;
    if (host === "www.cbc.ca") {
        environment = "production";
    } else if (host === "ocelot-staging.cbc.ca") {
        environment = "staging";
    } else if (host.startsWith("ocelot-") && !host.startsWith("ocelot-de-")) {
        environment = "qa";
    } else if (isSentryDevDebug) {
        environment = "dev";
    }
    if (environment !== null) {
        Sentry.init({
            dsn: isSentryDevDebug ? DEV_DSN_KEY : PROD_DSN_KEY,
            release: "ocelot@" + appVersion,
            integrations: [
                Sentry.reactRouterV5BrowserTracingIntegration({
                    history,
                    enableInp: true,
                    shouldCreateSpanForRequest: (url) => {
                        return !url.match(/(\/cookie-jar\/|\/comscore.json)/);
                    },
                }),
                Sentry.thirdPartyErrorFilterIntegration({
                    // List of application keys not considered third party. Value defined in Sentry bundler plugin as "applicationKey".
                    filterKeys: ["ocelot"],
                    // Error handling behaviour for errors containing third party stack frames.
                    behaviour:
                        "apply-tag-if-exclusively-contains-third-party-frames",
                }),
                // Extracts all non-native attributes from the error object and attaches them to the event as extra data
                Sentry.extraErrorDataIntegration(),
                // Adds module metadata to stack frames using the module_metadata property. Metadata is added to
                // modules at build time via the Sentry Webpack plugin. "scopeId" is used to identify the application
                // that the module belongs to (e.g. "scope-ocelot", "scope-membership").
                Sentry.moduleMetadataIntegration(),
                // Flag events that contain frames originating from specific applications (e.g. scope-membership)
                sentryEventScopeTagsIntegration(),
                sentrySetUserIDIntegration(),
            ],
            environment,
            allowUrls,
            denyUrls,
            ignoreErrors,
            tracesSampleRate,
            beforeSend: beforeSend(beforeSendFilter),
        });
    }
};
